<script lang="ts">
import { presets, getActiveValueLabelShared } from "@/assets/ts/diamondFilters";
export { presets, getActiveValueLabelShared };
</script>

<script setup lang="ts">
import { defineProps, defineEmits, ref, computed } from "vue";
import DiamondsSearchFormSimple from "@/components/diamonds/form/diamondsSearchFormSimple.vue";
import DiamondsSearchFormPro from "@/components/diamonds/form/diamondsSearchFormPro.vue";
import {useI18n} from "vue-i18n";

const { t } = useI18n();
const props = withDefaults(defineProps<{
  filters: any;
  activeFilters: any;
  singleFilterKey?: string;
  simpleFilterKeys?: string[];
  searchMode?: 'simple' | 'pro';
}>(), {
  searchMode: 'simple'
});

const emit = defineEmits<{
  (e: "update:filters", value: any): void;
  (e: "updateFilters", value: any): void;
  (e: "update:searchMode", value: 'simple' | 'pro'): void;
}>();

const internalSearchMode = ref<'simple' | 'pro'>(props.searchMode);
const searchMode = computed({
  get: () => props.searchMode || internalSearchMode.value,
  set: (val: 'simple' | 'pro') => {
    internalSearchMode.value = val;
    emit('update:searchMode', val);
  }
});
const manualExpanded = ref<Record<string, boolean>>({});

const updateFilters = (value: any) => {
  emit("update:filters", value);
}

const switchToSimple = () => {
  searchMode.value = 'simple';
  const newFilters = { ...props.activeFilters };
  const simpleKeys = props.simpleFilterKeys || ['type', 'size', 'form'];

  Object.keys(props.filters).forEach(key => {
    if (!simpleKeys.includes(key)) {
      newFilters[key] = props.filters[key].default !== undefined ? props.filters[key].default : null;
    }
  });

  emit("update:filters", newFilters);
};

const isFilterDefined = (key: string) => {
  const filter = props.filters[key];
  const active = props.activeFilters[key];
  if (!active) return false;
  // Handle objects (like carat or price range)
  if (typeof active === 'object' && filter.default && typeof filter.default === 'object') {
    return active.min !== filter.default.min || active.max !== filter.default.max;
  }
  if (Array.isArray(active) && Array.isArray(filter.default)) {
    return active.length !== filter.default.length || !active.every((v, i) => v === filter.default[i]);
  }
  if (filter.default && active === filter.default) return false;
  return true;
};

const isFilterOpen = (key: string) => {
  if (isFilterDefined(key)) {
    return true;
  }
  if (manualExpanded.value[key] !== undefined) {
    return manualExpanded.value[key];
  }
  return props.filters[key]?.visible || false;
};

const toggleFilter = (key: string) => {
  if (isFilterDefined(key)) return; // Always open if defined
  manualExpanded.value[key] = !isFilterOpen(key);
};

const filteredPresets = (activeFilters: { type: any; stone_type: any; form: any; stone_form: any; }, rawPresets: any[] = []) => {
  const stoneType = (activeFilters.type ?? activeFilters.stone_type) as string | undefined;
  const stoneForm = (activeFilters.form ?? activeFilters.stone_form) as string | undefined;

  return rawPresets.filter((preset) => preset.filters.type === stoneType && preset.forms.includes(stoneForm));
};

const isPresetActive = (activeFilters: { [x: string]: any; }, presetFilters: any) => {
  return Object.entries(presetFilters).every(([key, value]) => {
    const activeValue = activeFilters[key];
    if (Array.isArray(value) && Array.isArray(activeValue)) {
      return value.length === activeValue.length && value.every(v => activeValue.includes(v));
    }
    return activeValue === value;
  });
};

const updateFilter = (key: string, value: any) => {
  let newFilters = { ...props.activeFilters };
  const filter = props.filters[key];

  let parsedValue = value;
  if (filter.type === 'bool') {
    if (value === 'true') parsedValue = true;
    else if (value === 'false') parsedValue = false;
  }

  const oldValue = newFilters[key];

  // If it's the new list component, we always want the array value it emits
  if (filter.type === 'text' && filter.view === 'list') {
    newFilters[key] = value;
  } else if (newFilters[key] === parsedValue) {
    newFilters[key] = filter.default !== undefined ? filter.default : null;
  } else {
    newFilters[key] = parsedValue;
  }

  // Special rule sets
  if (key === 'type' && oldValue === 'earth' && newFilters.type === 'lab') {
    newFilters.institute = [];
    newFilters.color = ["E", "D"];
    newFilters.clarity = ["VS2", "VS1", "VVS2", "VVS1", "IF", "FL"];
  }

  if (key === 'type' && oldValue === 'lab' && newFilters.type === 'earth' && newFilters.colored === true) {
    newFilters.colored = false;
    newFilters.color = [];
    newFilters.fancy_intensity = [];
  }

  if (key === 'form' && oldValue === 'BR' && newFilters.form !== 'BR') {
    newFilters.clarity = ["VS2", "VS1", "VVS2", "VVS1", "IF", "FL"];
    newFilters.symmetry = ["VG", "EX"];
    newFilters.polish = ["VG", "EX"];
  }

  if (key === 'colored' && (oldValue === false || oldValue == null) && newFilters.colored === true) {
    newFilters.type = "lab";
    newFilters.clarity = [];
    newFilters.color = [];
    newFilters.cut = [];
    newFilters.polish = [];
    newFilters.fancy_intensity = [];
    newFilters.symmetry = [];
    newFilters.fluorescence = [];
    newFilters.institute = [];
    newFilters.fastDelivery = false;
  }

  if (key === 'form' && newFilters.form !== "BR") {
    newFilters.cut = [];
  }

  if ((key == "form" || key == "type") && !(typeof newFilters.colored == "boolean" && newFilters.colored === true)) {
    const presetFilters = filteredPresets(newFilters, presets)[0];
    let activePreset = false;

    for (const preset of presets) {
      activePreset = activePreset || isPresetActive(newFilters, preset.filters);
    }

    if (!activePreset) {

      newFilters = { ...newFilters, ...presetFilters.filters };
    }
  }

  emit("update:filters", newFilters);
};

const getFilterLabel = (key: string) => {
  return t('components.diamonds.search.labels.' + key);
};

const getActiveValueLabel = (key: string, value: any) => {
  return getActiveValueLabelShared(key, value, props.filters, props.activeFilters);
};

const resetFilters = () => {
  const newFilters: Record<string, any> = {};
  Object.keys(props.filters).forEach((key) => {
    const def = props.filters[key]?.default;
    newFilters[key] = def !== undefined ? def : null;
  });
  emit("update:filters", newFilters);
};

</script>

<template>
  <div class="diamonds-search-form w-full mx-auto p-4 pt-0">
    <!-- Switch between Simple and Pro -->
    <div v-if="false" class="flex justify-center mb-10">
      <div class="relative bg-gray-100 p-1 rounded-xl flex w-full shadow-inner">
        <!-- Sliding Background -->
        <div
          class="absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-lg shadow-sm transition-all duration-300 ease-out transform"
          :style="{ left: searchMode === 'pro' ? 'calc(50% + 2px)' : '4px' }"
        ></div>

        <!-- Buttons -->
        <button
          type="button"
          @click="switchToSimple"
          class="relative flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors duration-200 z-10"
          :class="searchMode === 'simple' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16m-7 6h7" />
          </svg>
          Einfach
        </button>
        <button
          type="button"
          @click="searchMode = 'pro'"
          class="relative flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors duration-200 z-10"
          :class="searchMode === 'pro' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
          Erweitert
        </button>
      </div>
    </div>

    <div v-if="searchMode === 'simple'">
      <DiamondsSearchFormSimple
        :filters="filters"
        :active-filters="activeFilters"
        :simple-filter-keys="simpleFilterKeys"
        :get-filter-label="getFilterLabel"
        :get-active-value-label="getActiveValueLabel"
        :is-filter-defined="isFilterDefined"
        :presets="presets"
        @update-filter="updateFilter"
        @update-filters="updateFilters"
      />
    </div>

    <div v-else>
      <DiamondsSearchFormPro
        :filters="filters"
        :active-filters="activeFilters"
        :single-filter-key="singleFilterKey"
        :get-filter-label="getFilterLabel"
        :get-active-value-label="getActiveValueLabel"
        :is-filter-defined="isFilterDefined"
        :is-filter-open="isFilterOpen"
        :toggle-filter="toggleFilter"
        :presets="presets"
        @update-filter="updateFilter"
        @update-filters="updateFilters"
      />
    </div>
    <div class="mt-8 flex justify-center mb-10">
      <button
        @click="resetFilters"
        class="text-xs uppercase tracking-widest text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        {{ t('components.catalog.filterSideBar.cta.reset') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
</style>